import { create } from 'zustand'
import { equipment_status } from '@prisma/client';
import { createJSONStorage, persist } from 'zustand/middleware';
import { AppointmentStatus } from '~/types/server-types/AppointmentStatus';
import LocalDateTime from '~/lib/localdatetime';

export type DateRange = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
};

export type Room = {
    id: number;
    name: string;
    storeId: number;
    room_category: {
        id: number;
        name: string;
    } | null;
}

export type Equipment = {
    id: number;
    name: string;
    storeId: number;
    status: equipment_status;
    equipment_category: {
        id: number;
        name: string;
    } | null;
}

export type Practitioner = {
    id: number;
    firstName: string;
    lastName: string;
    storeIds: number[];
    practitioner_category: {
        id: number;
        name: string;
    } | null;
}

export type Store = {
    id: number;
    label: string;
}

export type CalendarViewMode =
    | 'dayGridMonth'
    | 'timeGridWeek'
    | 'timeGridThreeDays'
    | 'resourceTimeGridDay';

export type AppointmentFilters = {
    status: Set<AppointmentStatus>;
}

type AppointmentsCalendarState = {
    // Store (clinic location)
    selectedStore: Store | undefined;

    // Drawer states
    customerDrawerOpen: boolean;
    appointmentDrawerOpen: boolean;
    createOrModifyAppointmentDrawerOpen: boolean;

    // Filters
    appointmentFilters: AppointmentFilters;
    roomFilters: Set<string>;
    equipmentFilters: Set<string>;
    practitionerFilters: Set<string>;

    // Resources
    allRooms: Room[];
    allEquipment: Equipment[];
    allPractitioners: Practitioner[];

    // Date range
    selectedDateRange: DateRange;

    // View mode
    viewMode: CalendarViewMode;

    // Actions
    setSelectedStore: (store: Store) => void;
    setCustomerDrawerOpen: (isOpen: boolean) => void;
    setAppointmentDrawerOpen: (isOpen: boolean) => void;
    setCreateOrModifyAppointmentDrawerOpen: (isOpen: boolean) => void;

    setAppointmentFilters: (filters: AppointmentFilters) => void;
    setRoomFilters: (filters: Set<string>) => void;
    setEquipmentFilters: (filters: Set<string>) => void;
    setPractitionerFilters: (filters: Set<string>) => void;

    setSelectedDateRange: (dateRange: DateRange) => void;
    setAllRooms: (rooms: Room[]) => void;
    setAllEquipment: (equipment: Equipment[]) => void;
    setAllPractitioners: (practitioners: Practitioner[]) => void;
    setViewMode: (viewMode: CalendarViewMode) => void;
};

export const useAppointmentsCalendarStore = create<AppointmentsCalendarState>()(
    persist(
        (set) => ({
            // Initial state
            selectedStore: undefined,
            customerDrawerOpen: false,
            appointmentDrawerOpen: false,
            createOrModifyAppointmentDrawerOpen: false,
            roomFilters: new Set<string>(),
            equipmentFilters: new Set<string>(),
            practitionerFilters: new Set<string>(),
            appointmentFilters: {
                status: new Set<AppointmentStatus>(
                    Object.values(AppointmentStatus).filter((val) => val !== AppointmentStatus.CANCELLED && val !== AppointmentStatus.DECLINED && val !== AppointmentStatus.CALLED_OFF)
                ),
            },
            allRooms: [],
            allEquipment: [],
            allPractitioners: [],
            selectedDateRange: {
                startDate: LocalDateTime.fromUTCDate(new Date(), "Europe/Berlin"),
                endDate: LocalDateTime.fromUTCDate(new Date(), "Europe/Berlin"),
                // endDate: new Date(new Date().setDate(new Date().getDate() + 2))
            },
            viewMode: 'resourceTimeGridDay',

            // Actions
            setSelectedStore: (store: Store) => set({ selectedStore: store }),
            setCustomerDrawerOpen: (isOpen: boolean) => set({ customerDrawerOpen: isOpen }),
            setAppointmentDrawerOpen: (isOpen: boolean) => set({ appointmentDrawerOpen: isOpen }),
            setCreateOrModifyAppointmentDrawerOpen: (isOpen: boolean) => set({ createOrModifyAppointmentDrawerOpen: isOpen }),
            setAppointmentFilters: (filters: AppointmentFilters) => set({ appointmentFilters: filters }),
            setRoomFilters: (filters: Set<string>) => set({ roomFilters: filters }),
            setEquipmentFilters: (filters: Set<string>) => set({ equipmentFilters: filters }),
            setPractitionerFilters: (filters: Set<string>) => set({ practitionerFilters: filters }),
            setSelectedDateRange: (dateRange: DateRange) => set({ selectedDateRange: dateRange }),
            setAllRooms: (rooms: Room[]) => set({ allRooms: rooms }),
            setAllEquipment: (equipment: Equipment[]) => set({ allEquipment: equipment }),
            setAllPractitioners: (practitioners: Practitioner[]) => set({ allPractitioners: practitioners }),
            setViewMode: (viewMode: CalendarViewMode) => set({ viewMode }),
        }),
        {
            name: "appointments-calendar-store",
            partialize: (state) => ({
                selectedStore: state.selectedStore,
                // viewMode: state.viewMode,
            }),
            storage: createJSONStorage(() => sessionStorage),
        }
    )
)